import { useCallback } from 'react';
import {
  InboxLocationUserSetting,
  SettingType_Enum,
} from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { OutboundMessageStatus } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { InboxView, ThreadReadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { RelatedType, Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { ManualSmsScheduledV1 } from '@frontend/api-manual-scheduled-sms';
import { MessagingSettingsV1 } from '@frontend/api-messaging-settings';
import { SMSDataSharedTypes, SMSDataSharedUtils, SMSDataV3, SMSDataV4 } from '@frontend/api-sms-data';
import { SMSSendV3 } from '@frontend/api-sms-send';
import { getUser } from '@frontend/auth-helpers';
import { useMessageAction } from '@frontend/contact-actions-send-message';
import { useTranslation } from '@frontend/i18n';
import { MiniChatHooks } from '@frontend/mini-chat';
import { notificationSounds, useSmsMessageNotification } from '@frontend/notifications';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { ThreadSendingAreaStores, ThreadSendingAreaUtils } from '@frontend/thread-sending-area';
import { GetWeavePopNotificationByType } from '@frontend/types';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';
import { useAlignCountAndListV4 } from './use-align-count-and-list-v4';

/**
 * A hook that returns a function to execute when a SMSNotification event is received.
 * This doesn't have any WebSocket subscription logic, just the handler. Useful for testing.
 */
export const useSMSDataV3WSHandler = () => {
  const { t } = useTranslation('notifications');
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const user = getUser();
  const sendMutation = SMSSendV3.Mutations.useSendMutation();
  const unknownNumberString = t('Unknown');
  const { onClick: onView } = useMessageAction();
  const { create: createNotification } = useSmsMessageNotification({
    onRespond: (notification, response) => {
      return sendMutation.mutateAsync({
        locationId: notification.payload.locationId,
        personPhone: notification.payload.number,
        body: response,
        media: [],
        programSlugId: 'manual-messages',
        personId: notification.payload.personId,
        createdBy: user?.userID,
        departmentId: notification.payload.departmentId,
        messageType: ThreadSendingAreaUtils.getMessageTypeFromRelatedIds([]),
      });
    },
    onView: (notification) => {
      onView({
        threadId: notification.payload.threadId,
        threadGroupId: notification.payload.locationId,
        personPhone: notification.payload.number === unknownNumberString ? undefined : notification.payload.number,
        personId: notification.payload.personId,
        departmentId: notification.payload.departmentId,
        groupIds: [notification.payload.locationId],
      });
    },
  });
  useAlignCountAndListV4();

  const { getFeatureFlagValue, featureFlags } = useAppFlagStore();
  const shouldQueryMessagingSettings = getFeatureFlagValue({
    flagName: 'nwx:messaging-user-settings-sms-messages',
    locationIds: selectedLocationIds,
    strategy: 'ANY',
  });

  const defaultMessagingSettingsQuery = MessagingSettingsV1.Queries.useTypesQuery(
    {},
    {
      enabled: shouldQueryMessagingSettings,
      select: (data) => {
        const defaultSetting = data.types.find(
          (item) => item.settingType === SettingType_Enum.INBOX_LOCATION_USER_SETTING
        )?.defaultSetting;
        if (defaultSetting && 'inboxLocationUserSetting' in defaultSetting) {
          return defaultSetting.inboxLocationUserSetting;
        }
        return undefined;
      },
    }
  );

  const messagingSettingsQuery = MessagingSettingsV1.Queries.useListQuery(
    {
      settingType: SettingType_Enum.INBOX_LOCATION_USER_SETTING,
      locationIds: selectedLocationIds,
      orgId: selectedOrgId,
      userId: user?.userID ?? '',
    },
    {
      enabled: shouldQueryMessagingSettings && !!user?.userID,
      select: (data) => {
        return selectedLocationIds.reduce<Record<string, InboxLocationUserSetting>>((acc, locationId) => {
          const matchingSetting = data.settings.find((item) => item.locationId === locationId);

          // If no setting has been configured, there will be no setting for that location in the list
          // So we will populate that location's setting with the default if it's not in the list
          if (!matchingSetting || !('inboxLocationUserSetting' in matchingSetting.setting)) {
            acc[locationId] =
              defaultMessagingSettingsQuery.data ??
              MessagingSettingsV1.Constants.DEFAULT_SETTINGS.inboxLocationUserSetting;
            return acc;
          }

          acc[locationId] = matchingSetting.setting.inboxLocationUserSetting;
          return acc;
        }, {});
      },
    }
  );

  const messagingUserSettingsQuery = MessagingSettingsV1.Queries.useGetQuery(
    {
      settingType: SettingType_Enum.INBOX_USER_SETTING,
      userId: user?.userID ?? '',
      orgId: selectedOrgId,
    },
    {
      enabled: shouldQueryMessagingSettings && !!user?.userID,
      select: (data) => {
        if ('inboxUserSetting' in data.setting) {
          return data.setting.inboxUserSetting;
        }
        return undefined;
      },
    }
  );

  const handleNotification = useCallback<typeof createNotification>(
    (payload, options) => {
      const locationId = payload.payload.locationId;
      const newSettingsShouldBeChecked = !!featureFlags[locationId]?.get('nwx:messaging-user-settings-sms-messages')
        ?.value;
      const soundId =
        notificationSounds.find(
          (sound) => sound.name.toLowerCase() === messagingUserSettingsQuery.data?.notificationSound.toLowerCase()
        )?.id ?? '';

      // TODO: implement checking if this is an appointment confirmation once the BE can tell us if it is

      const shouldNotify = newSettingsShouldBeChecked
        ? !!messagingSettingsQuery.data?.[locationId]?.enableMessageNotifications
        : true;

      const title =
        `${payload.payload.firstName || ''} ${payload.payload.lastName || ''}`.trim() ||
        formatPhoneNumber(payload.payload.number);

      const resolvedOptions: typeof options = {
        ...options,
        os: {
          ...options?.os,
          title,
          body: payload.payload.body,
        },
        // Overriding the normal notification settings if using the new messaging settings instead
        force: newSettingsShouldBeChecked,
        soundId: newSettingsShouldBeChecked ? soundId : undefined,
      };

      if (shouldNotify) {
        createNotification(payload, resolvedOptions);
      }
    },
    [createNotification, featureFlags, messagingUserSettingsQuery.data?.notificationSound, messagingSettingsQuery.data]
  );

  const { upsertSMS, updateSMS, updateThread, updateThreadsArchivedStatus, updateThreadsActionableStatus } =
    SMSDataV3._QueryUpdaters.useQueryUpdaters();
  const smsV4QueryUpdaters = SMSDataV4._QueryUpdaters.useQueryUpdaters();
  const { updateScheduledSms } = ManualSmsScheduledV1._QueryUpdaters.useQueryUpdaters();
  const { setThreadTypingStatus } = ThreadSendingAreaStores.useTypingIndicatorStore();
  const { incrementThreadUnreadCount, resetThreadUnreadCount } = MiniChatHooks.useMiniChatShallowStore(
    'incrementThreadUnreadCount',
    'resetThreadUnreadCount'
  );

  const v4ThreadCountUpdater = useCallback(
    async ({
      threadIds,
      locationId,
      newStatuses,
    }: {
      threadIds: string[];
      locationId: string;
      newStatuses: SMSDataSharedTypes.CountThreadsStatusUpdate[];
    }) => {
      newStatuses.forEach((newStatus) => {
        smsV4QueryUpdaters.updateThreadCounts({
          threadIds,
          locationId,
          newStatus,
          isOptimistic: false,
        });
      });
    },
    [smsV4QueryUpdaters]
  );

  const wsEventHandler = useCallback<GetWebsocketEventHandler<'SMSNotification'>>(
    (event) => {
      const { params } = event;
      const newSMS =
        params.action === 'inbound' || params.action === 'outbound'
          ? SMSDataSharedUtils.convertWSPayloadToSMS(params)
          : undefined;

      switch (params.action) {
        case 'inbound': {
          handleNotification({
            id: String(event.id),
            type: 'sms-message-new',
            payload: {
              body: params.body,
              number: params.patient_number || params.phone_mobile || unknownNumberString,
              personId: params.person_id,
              threadId: params.thread_ids[0] || '',
              departmentId: params.department_id,
              locationId: params.location_id,
              firstName: params.first_name,
              lastName: params.last_name,
            },
            timestamp: Date.now(),
            state: {
              paused: false,
              status: 'unread',
            },
            flags: {
              shouldAllowReply: true,
            },
          } satisfies GetWeavePopNotificationByType<'sms-message-new'>);

          if (newSMS) {
            upsertSMS({
              sms: newSMS,
            });
            smsV4QueryUpdaters.upsertThread({
              matchValues: {
                threadId: newSMS.threadId,
                locationId: newSMS.locationId,
              },
              updater: (thread) =>
                thread ? SMSDataV4._QueryUpdaters.Utils.upsertSmsOntoThread(thread, newSMS) : undefined,
              isOptimistic: false,
              isAutomated: !!newSMS.autogeneratedBy,
            });
            // Wait for classification to finish before refetching the count
            // TODO: remove this in favor of status update event once classification BE project is done
            setTimeout(() => {
              smsV4QueryUpdaters.invalidateQueries({
                endpointName: 'CountThreads',
                queryFilters: {
                  queryKey: smsV4QueryUpdaters.getQueryKey<'CountThreads'>({
                    endpointName: 'CountThreads',
                    request: {
                      filter: {
                        inboxView: InboxView.ACTIONABLE,
                      },
                    },
                  }),
                  exact: false,
                  predicate: ({ queryKey }) => {
                    return !!queryKey[2]?.locationIds?.includes(newSMS.locationId);
                  },
                },
              });
              smsV4QueryUpdaters.invalidateQueries({
                endpointName: 'CountThreads',
                queryFilters: {
                  exact: false,
                  predicate: ({ queryKey }) => {
                    const request = queryKey[2];
                    return (
                      !!request?.locationIds?.includes(newSMS.locationId) &&
                      !!request.filter?.statuses?.includes(ThreadReadStatus.NEW)
                    );
                  },
                },
              });
            }, 1000);
          }
          incrementThreadUnreadCount(params.thread_ids[0]);

          break;
        }
        case 'outbound':
          if (newSMS) {
            upsertSMS({
              sms: newSMS,
            });
            smsV4QueryUpdaters.upsertThread({
              matchValues: {
                threadId: newSMS.threadId,
                locationId: newSMS.locationId,
              },
              updater: (thread) =>
                thread ? SMSDataV4._QueryUpdaters.Utils.upsertSmsOntoThread(thread, newSMS) : undefined,
              isOptimistic: false,
              isAutomated: !!newSMS.autogeneratedBy,
            });

            // Update scheduled messages that have been sent in cache
            params.related_ids?.forEach((relatedId) => {
              if (relatedId.type !== RelatedType.RELATED_TYPE_SCHEDULED_MESSAGE_ID) return;
              updateScheduledSms({
                matchValues: {
                  id: relatedId.id,
                  locationId: newSMS.locationId,
                  threadId: newSMS.threadId,
                },
                newValues: {
                  status: OutboundMessageStatus.SENT,
                },
              });
            });
          }

          break;
        case 'status': {
          const newStatus = SMSDataSharedUtils.convertStringToStatus(params.status);
          updateSMS({
            matchValues: {
              id: params.uuid,
              locationId: params.location_id,
            },
            newValues: {
              status: newStatus,
            },
          });
          smsV4QueryUpdaters.upsertSms({
            matchValues: {
              smsId: params.uuid,
              threadId: params.thread_ids?.[0] ?? '',
              locationId: params.location_id,
            },
            updater: (oldSms) => (oldSms ? { ...oldSms, status: newStatus } : undefined),
            isOptimistic: false,
            isAutomated: undefined,
          });
          if (
            newStatus === Status.STATUS_READ ||
            newStatus === Status.STATUS_NEW ||
            newStatus === Status.STATUS_ERROR
          ) {
            smsV4QueryUpdaters.updateThreadCounts({
              threadIds: params.thread_ids,
              locationId: params.location_id,
              newStatus:
                newStatus === Status.STATUS_READ ? 'read' : newStatus === Status.STATUS_NEW ? 'unread' : 'error',
            });
          }
          break;
        }
        case 'smsTagApplied':
          updateSMS({
            matchValues: {
              id: params.sms_id,
              threadId: params.thread_ids?.length ? params.thread_ids[0] : undefined,
              locationId: params.location_id,
            },
            newValues: (oldSms) => {
              const newTags = Array.from(new Set([...oldSms.tags, params.tag_id]));
              return {
                ...oldSms,
                tags: newTags,
                tagsDetailed: newTags.map((tagId) => ({
                  tagId,
                  smsId: oldSms.id,
                  smsCreatedAt: oldSms.createdAt,
                  appliedBy: params.weave_user_id,
                })),
              };
            },
            newThreadValues: {
              tagId: params.tag_id,
              tagInfo: {
                tagId: params.tag_id,
                smsId: params.sms_id,
                smsCreatedAt: params.sms_created_at,
                appliedBy: params.weave_user_id,
              },
            },
            skipInvalidation: true,
          });
          smsV4QueryUpdaters.upsertThread({
            matchValues: {
              threadId: params.thread_ids?.[0] ?? '',
              locationId: params.location_id,
            },
            updater: (thread) => {
              if (!thread) return undefined;

              return {
                ...thread,
                uniqueTags: SMSDataSharedUtils.dedupeSMSTagsArray([
                  ...(thread.uniqueTags ?? []),
                  {
                    tagId: params.tag_id,
                    smsId: params.sms_id,
                    smsCreatedAt: params.sms_created_at,
                    appliedBy: params.weave_user_id,
                  },
                ]),
              };
            },
            isOptimistic: false,
            isAutomated: undefined,
          });
          break;
        case 'smsTagDismissed':
          updateSMS({
            matchValues: {
              id: params.sms_id,
              threadId: params.thread_ids?.length ? params.thread_ids[0] : undefined,
              locationId: params.location_id,
            },
            newValues: (oldSms) => ({
              ...oldSms,
              tags: oldSms.tags.filter((tagId) => tagId !== params.tag_id),
              tagsDetailed: oldSms.tagsDetailed?.filter(({ tagId }) => tagId !== params.tag_id),
            }),
            newThreadValues: {
              tagId: params.tag_id,
              isDismissed: true,
              tagInfo: {
                tagId: params.NextSMSWTag.tag_id,
                smsId: params.NextSMSWTag.sms_id,
                smsCreatedAt: params.NextSMSWTag.sms_created_at,
                appliedBy: params.NextSMSWTag.applied_by,
              },
            },
            skipInvalidation: true,
          });
          if (params.thread_ids?.[0]) {
            const newTag = params.NextSMSWTag;
            smsV4QueryUpdaters.upsertThread({
              matchValues: {
                threadId: params.thread_ids[0],
                locationId: params.location_id,
              },
              updater: (oldThread) => {
                if (!oldThread || !oldThread.uniqueTags) return undefined;
                const existingTags = oldThread.uniqueTags;
                if (!existingTags) return undefined;

                const shouldUpdateTag = !!newTag.tag_id;
                if (shouldUpdateTag) {
                  return {
                    ...oldThread,
                    uniqueTags: existingTags.map((tag) =>
                      tag.tagId === newTag.tag_id
                        ? {
                            tagId: newTag.tag_id,
                            smsId: newTag.sms_id,
                            smsCreatedAt: newTag.sms_created_at,
                            appliedBy: newTag.applied_by,
                          }
                        : tag
                    ),
                  };
                }

                return {
                  ...oldThread,
                  uniqueTags: existingTags.filter((tag) => tag.tagId !== params.tag_id),
                };
              },
              isOptimistic: false,
              isAutomated: undefined,
            });
          }
          break;
        case 'archived':
        case 'unarchived':
          if (params.thread_ids?.length && !!params.location_id) {
            updateThreadsArchivedStatus({
              threadIds: params.thread_ids,
              locationId: params.location_id,
              groupIds: [params.location_id],
              archived: params.action === 'archived',
            });
            params.thread_ids.forEach((threadId) => {
              smsV4QueryUpdaters.upsertThread({
                matchValues: {
                  threadId,
                  locationId: params.location_id!,
                },
                updater: (oldThread) =>
                  oldThread ? { ...oldThread, isArchived: params.action === 'archived' } : undefined,
                isOptimistic: false,
                isAutomated: undefined,
              });
            });
          }
          break;
        case 'bulkMarkRead':
        case 'markedRead':
          params.thread_ids?.forEach((threadId) => {
            updateThread({
              matchValues: {
                id: threadId,
                locationId: params.location_id,
              },
              newValues: {
                status: 'read',
              },
            });
            resetThreadUnreadCount(threadId);
            const locationId = params.location_id;
            if (locationId) {
              smsV4QueryUpdaters.upsertThread({
                matchValues: {
                  threadId: threadId,
                  locationId,
                },
                updater: (oldThread) =>
                  oldThread
                    ? {
                        ...oldThread,
                        status: 'read',
                      }
                    : undefined,
                isOptimistic: false,
                isAutomated: undefined,
              });
              smsV4QueryUpdaters.updateThreadCounts({
                threadIds: params.thread_ids ?? [],
                locationId,
                newStatus: 'read',
              });
            }
          });
          break;
        case 'bulkMarkUnread':
        case 'markedUnread':
          params.thread_ids?.forEach((threadId) => {
            updateThread({
              matchValues: {
                id: threadId,
                locationId: params.location_id,
              },
              newValues: {
                status: 'new',
              },
            });
            const locationId = params.location_id;
            if (locationId) {
              smsV4QueryUpdaters.upsertThread({
                matchValues: {
                  threadId,
                  locationId,
                },
                updater: (oldThread) => (oldThread ? { ...oldThread, status: 'new' } : undefined),
                isOptimistic: false,
                isAutomated: undefined,
              });
              smsV4QueryUpdaters.updateThreadCounts({
                threadIds: params.thread_ids ?? [],
                locationId,
                newStatus: 'unread',
              });
            }
          });
          break;
        case 'readStatusChange':
          params.thread_ids?.forEach((threadId) => {
            updateThread({
              matchValues: {
                id: threadId,
                locationId: params.location_id,
              },
              newValues: {
                status: params.status,
              },
            });
            if (params.status === 'read') {
              resetThreadUnreadCount(threadId);
            }
            const locationId = params.location_id;
            if (locationId) {
              smsV4QueryUpdaters.upsertThread({
                matchValues: {
                  threadId,
                  locationId,
                },
                updater: (oldThread) => (oldThread ? { ...oldThread, status: params.status } : undefined),
                isOptimistic: false,
                isAutomated: undefined,
              });
            }
          });
          break;
        case 'inboundThreadUpdate':
          if (params.location_id) {
            const newSms = SMSDataSharedUtils.convertWSPayloadToSMS(params);
            upsertSMS({
              sms: SMSDataSharedUtils.convertWSPayloadToSMS(params),
            });

            smsV4QueryUpdaters.upsertSms({
              matchValues: {
                smsId: newSms.id,
                threadId: newSms.threadId,
                locationId: newSms.locationId,
              },
              updater: () => newSms,
              isOptimistic: false,
              isAutomated: !!newSms.autogeneratedBy,
            });
          }
          break;
        case 'typing':
          if (user?.userID && params.weave_user_id === user?.userID) break;
          setThreadTypingStatus({
            locationId: params.location_id,
            threadId: params.thread_id,
            userId: params.weave_user_id,
            firstName: params.first_name,
            lastName: params.last_name,
            isTyping: params.is_typing,
          });
          break;
        case 'updateActionableStatus':
          if (params.location_id && params.actionable !== undefined) {
            updateThreadsActionableStatus({
              locationId: params.location_id,
              actionable: params.actionable,
              threadIds: params.thread_ids ?? [],
            });
            const newStatus = params.actionable ? 'actionable' : 'not-actionable';
            v4ThreadCountUpdater({
              threadIds: params.thread_ids ?? [],
              locationId: params.location_id,
              newStatuses: [newStatus],
            });
          }
          break;
      }
    },
    [
      handleNotification,
      incrementThreadUnreadCount,
      resetThreadUnreadCount,
      setThreadTypingStatus,
      smsV4QueryUpdaters,
      unknownNumberString,
      updateSMS,
      updateScheduledSms,
      updateThread,
      updateThreadsActionableStatus,
      updateThreadsArchivedStatus,
      upsertSMS,
      user?.userID,
      v4ThreadCountUpdater,
    ]
  );

  return wsEventHandler;
};

export const useSMSDataV3WSSubscription = () => {
  const handler = useSMSDataV3WSHandler();
  return useWebsocketEventSubscription('SMSNotification', handler);
};
